import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Form, Input, message, Typography } from 'antd';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    PlusOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LanguageMenu from "../common/header/LanguageSelect";
import { VERDICTIA_WORDMARK_LIGHT_SRC } from "../../brand/verdictiaBrand";
import { checkAuth, register_user } from "../../Services/user.service";
import {
    getPendingBillingPlan,
    isPaidPlan,
    normalizePublicPlan,
    savePendingBillingPlan,
} from "../../Services/pendingBillingPlan.service";
import { getAccessToken } from "../../utils/accessToken";
import { isCookieAuthMode } from "../../utils/authMode";
import './login.css';
import './register.css';

const { Text } = Typography;

const TERMS_URL = process.env.REACT_APP_TERMS_URL
    || "https://www.verdictia.com/legal/cgu/index.html";

// Legacy registration payload: remove when audience_profile is retired from the API contract.
const LEGACY_AUDIENCE_PROFILE = "other";

const PUBLIC_EMAIL_DOMAINS = new Set([
    "aol.com",
    "gmail.com",
    "googlemail.com",
    "hotmail.com",
    "hotmail.fr",
    "icloud.com",
    "live.com",
    "live.fr",
    "mail.com",
    "msn.com",
    "orange.fr",
    "outlook.com",
    "outlook.fr",
    "proton.me",
    "protonmail.com",
    "wanadoo.fr",
    "yahoo.ca",
    "yahoo.com",
    "yahoo.fr",
    "ymail.com",
]);

const PUBLIC_EMAIL_ROOTS = new Set(["gmail", "googlemail", "yahoo"]);

const COMMON_SECOND_LEVEL_TLDS = new Set([
    "ac",
    "co",
    "com",
    "edu",
    "gouv",
    "gov",
    "net",
    "org",
]);

function getEmailDomain(email = "") {
    const normalized = String(email).trim().toLowerCase();
    const match = normalized.match(/^[^\s@]+@([a-z0-9.-]+\.[a-z]{2,})$/i);
    if (!match) return "";
    return match[1].replace(/\.+$/, "");
}

function isPublicEmailDomain(domain = "") {
    if (!domain) return true;
    const normalizedDomain = domain.toLowerCase();
    if (PUBLIC_EMAIL_DOMAINS.has(normalizedDomain)) return true;
    const parts = normalizedDomain.split(".").filter(Boolean);
    const root = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
    return PUBLIC_EMAIL_ROOTS.has(root);
}

function titleCaseDomainLabel(label = "") {
    return label
        .split(/[-_.]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
}

export function deriveOrganizationNameFromEmail(email = "") {
    const domain = getEmailDomain(email);
    if (!domain || isPublicEmailDomain(domain)) return "";

    const parts = domain.split(".").filter(Boolean);
    if (parts.length < 2) return "";

    const secondLevel = parts[parts.length - 2];
    const baseLabel = parts.length >= 3 && COMMON_SECOND_LEVEL_TLDS.has(secondLevel)
        ? parts[parts.length - 3]
        : secondLevel;

    return titleCaseDomainLabel(baseLabel).slice(0, 120);
}

function scorePassword(value) {
    if (!value) return 0;
    let score = 0;
    if (value.length >= 8) score += 1;
    if (value.length >= 12) score += 1;
    if (/[A-Z]/.test(value)) score += 1;
    if (/[0-9]/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;
    return Math.min(score, 5);
}

function OrganizationNameField({ form, loading }) {
    const { t } = useTranslation('login');
    const emailValue = Form.useWatch('email', form);
    const organizationNameValue = Form.useWatch('organizationName', form);
    const autoFilledOrganizationRef = useRef("");
    const organizationEditedRef = useRef(false);

    useEffect(() => {
        const inferredOrganization = deriveOrganizationNameFromEmail(emailValue);
        const currentOrganization = String(organizationNameValue || "").trim();
        const previousAutoFill = autoFilledOrganizationRef.current;

        if (!inferredOrganization) {
            if (previousAutoFill && currentOrganization === previousAutoFill) {
                form.setFieldsValue({ organizationName: "" });
                autoFilledOrganizationRef.current = "";
                organizationEditedRef.current = false;
            }
            return;
        }

        const canAutoFill = !currentOrganization
            || (!organizationEditedRef.current && currentOrganization === previousAutoFill);

        if (!canAutoFill || currentOrganization === inferredOrganization) return;

        form.setFieldsValue({ organizationName: inferredOrganization });
        autoFilledOrganizationRef.current = inferredOrganization;
        organizationEditedRef.current = false;
    }, [emailValue, form, organizationNameValue]);

    const handleOrganizationNameChange = useCallback((event) => {
        const nextValue = String(event?.target?.value || "").trim();
        if (!nextValue) {
            organizationEditedRef.current = false;
            return;
        }
        organizationEditedRef.current = nextValue !== autoFilledOrganizationRef.current;
    }, []);

    return (
        <Form.Item
            className="register-field"
            label={t('register.fields.organization')}
            name="organizationName"
            rules={[
                { required: true, message: t('register.validation.organization_required') },
                { max: 120, message: t('register.validation.organization_max') }
            ]}
        >
            <Input
                size="small"
                placeholder={t('register.placeholders.organization')}
                autoComplete="organization"
                disabled={loading}
                onChange={handleOrganizationNameChange}
            />
        </Form.Item>
    );
}

function PasswordStrengthMeter({ form }) {
    const { t } = useTranslation('login');
    const passwordValue = Form.useWatch('password', form);
    const passwordStrength = useMemo(() => scorePassword(passwordValue), [passwordValue]);
    const strengthLevel = passwordStrength <= 2 ? 'weak' : passwordStrength <= 4 ? 'medium' : 'strong';

    return (
        <div
            className={`register-strength${passwordValue ? ' is-visible' : ''}`}
            data-level={strengthLevel}
            role="status"
            aria-live="polite"
        >
            <div className="register-strength-track" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                    <span
                        key={i}
                        className={`register-strength-seg${passwordStrength > i ? ' is-on' : ''}`}
                    />
                ))}
            </div>
            <span className="register-strength-label">
                {passwordValue ? t(`register.strength.${strengthLevel}`) : ''}
            </span>
        </div>
    );
}

function Register() {
    const { t } = useTranslation('login');
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [showJobFunction, setShowJobFunction] = useState(false);
    const stepHeadingRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const requestedPlan = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.has("plan") ? normalizePublicPlan(params.get("plan")) : null;
    }, [location.search]);
    const loginPath = requestedPlan ? `/?plan=${requestedPlan}` : "/";

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.has("plan")) {
            savePendingBillingPlan(requestedPlan);
        }
    }, [location.search, requestedPlan]);

    useEffect(() => {
        if (!isPaidPlan(requestedPlan)) return;
        if (getAccessToken()) {
            navigate(loginPath, { replace: true });
            return;
        }
        if (isCookieAuthMode()) {
            let mounted = true;
            checkAuth()
                .then((response) => {
                    const authPayload = response?.success !== undefined ? response : response?.data;
                    if (mounted && authPayload?.success && authPayload.data?.is_authenticated) {
                        navigate(loginPath, { replace: true });
                    }
                })
                .catch(() => {});
            return () => {
                mounted = false;
            };
        }
    }, [loginPath, navigate, requestedPlan]);

    const passwordRules = useMemo(() => ([
        { required: true, message: t('register.validation.password_required') },
        { min: 8, message: t('register.validation.password_min') },
        { pattern: /[A-Z]/, message: t('register.validation.password_uppercase') },
        { pattern: /[0-9]/, message: t('register.validation.password_number') },
        { pattern: /[^A-Za-z0-9]/, message: t('register.validation.password_special') }
    ]), [t]);

    const submit = async (values) => {
        const completedValues = {
            ...(form.getFieldsValue?.(true) || {}),
            ...values,
        };
        const payload = {
            name: completedValues.name.trim(),
            surname: completedValues.surname.trim(),
            email: completedValues.email.trim().toLowerCase(),
            password: completedValues.password,
            confirm_password: completedValues.confirmPassword,
            organization_name: completedValues.organizationName.trim(),
            audience_profile: LEGACY_AUDIENCE_PROFILE,
            job_function: completedValues.jobFunction?.trim() || null,
            accepted_terms: completedValues.acceptedTerms === true
        };

        setLoading(true);
        try {
            await register_user(payload);
            sessionStorage.setItem("register_success_message", t('register.success_login_message'));
            const pendingPlan = getPendingBillingPlan();
            navigate(pendingPlan ? `/?plan=${pendingPlan}` : "/");
        } catch (err) {
            const backendMessage =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail;
            if (err.response?.status === 429) {
                message.error(t('register.errors.rate_limited'));
            } else if (err.response?.status === 503) {
                message.error(t('register.errors.unavailable'));
            } else if (err.response?.status === 422) {
                message.error(t('register.errors.validation'));
            } else {
                message.error(backendMessage || t('register.errors.generic'));
            }
        } finally {
            setLoading(false);
        }
    };

    const focusStepHeading = useCallback(() => {
        const schedule = window.requestAnimationFrame || window.setTimeout;
        schedule(() => stepHeadingRef.current?.focus({ preventScroll: true }));
    }, []);

    const handleTypingFocus = useCallback(() => {
        document.body?.classList.add("login-performance-mode");
    }, []);

    useEffect(() => {
        return () => {
            document.body?.classList.remove("login-performance-mode");
        };
    }, []);

    const handleFormFinish = (values) => {
        if (currentStep === 0) {
            setCurrentStep(1);
            focusStepHeading();
            return;
        }
        submit(values);
    };

    const handleBackToDetails = () => {
        setCurrentStep(0);
        focusStepHeading();
    };

    const handleFinishFailed = ({ errorFields = [] }) => {
        const firstInvalidField = errorFields[0]?.name;
        if (!firstInvalidField) return;
        form.scrollToField?.(firstInvalidField, { block: 'center' });
        const schedule = window.requestAnimationFrame || window.setTimeout;
        schedule(() => form.getFieldInstance?.(firstInvalidField)?.focus?.());
    };

    return (
        <div className="login-body register-body register-flow-v2">
            <div className="login-ambient" aria-hidden="true" />
            <div className="login-layout">
                <div className="left-side register-left-side">
                    <div className="login-panel-texture" aria-hidden="true" />
                    <div className="header-section">
                        <div className="header-logo-login" aria-label="Verdictia">
                            <img src={VERDICTIA_WORDMARK_LIGHT_SRC} alt="Verdictia" width="174" height="58" />
                        </div>
                        <div className="language-menu-shell">
                            <LanguageMenu />
                        </div>
                    </div>

                    <div className="register-hero-panel">
                        <div className="register-hero-kicker">{t('register.hero.kicker')}</div>
                        <h1>{t('register.hero.title')}</h1>
                        <p>{t('register.hero.subtitle')}</p>
                        <div className="register-trial-strip" aria-hidden="true">
                            <span>{t('register.hero.trial')}</span>
                            <span>{t('register.hero.analysis')}</span>
                            <span>{t('register.hero.access')}</span>
                        </div>
                    </div>
                </div>

                <div className="right-side">
                    <div className="form-container register-form-container">
                        <div
                            className="form-content register-form-content"
                            onFocus={handleTypingFocus}
                        >
                            <div className="register-topbar">
                                <div className="register-account-shortcut">
                                    <span>{t('register.already_account')}</span>
                                    <button
                                        type="button"
                                        onClick={() => navigate(loginPath)}
                                        disabled={loading}
                                    >
                                        {t('register.sign_in')}
                                    </button>
                                </div>
                            </div>

                            <h2><b>{t('register.title')}</b></h2>
                            <Text className="register-subtitle">{t('register.subtitle')}</Text>

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleFormFinish}
                                onFinishFailed={handleFinishFailed}
                                autoComplete="on"
                                validateTrigger="onBlur"
                                className="register-form register-form--stepped"
                            >
                                <div className="register-stepper" role="list" aria-label={t('register.flow.progress_label')}>
                                    <div
                                        className={`register-stepper-item ${currentStep === 0 ? 'is-active' : 'is-complete'}`}
                                        role="listitem"
                                        aria-current={currentStep === 0 ? 'step' : undefined}
                                    >
                                        <span className="register-stepper-index">1</span>
                                        <span className="register-stepper-label">{t('register.flow.details_step')}</span>
                                    </div>
                                    <span className={`register-stepper-track ${currentStep === 1 ? 'is-complete' : ''}`} aria-hidden="true" />
                                    <div
                                        className={`register-stepper-item ${currentStep === 1 ? 'is-active' : ''}`}
                                        role="listitem"
                                        aria-current={currentStep === 1 ? 'step' : undefined}
                                    >
                                        <span className="register-stepper-index">2</span>
                                        <span className="register-stepper-label">{t('register.flow.security_step')}</span>
                                    </div>
                                </div>

                                {currentStep === 0 && (
                                    <div className="register-step-panel" key="details">
                                        <div className="register-step-copy">
                                            <span className="register-step-kicker">{t('register.flow.details_eyebrow')}</span>
                                            <h3 ref={stepHeadingRef} tabIndex={-1}>{t('register.flow.details_title')}</h3>
                                            <p>{t('register.flow.details_subtitle')}</p>
                                        </div>

                                <div className="register-section register-section--identity">
                                    <div className="register-section-header">
                                        <span className="register-section-step">01</span>
                                        <span className="register-section-title">{t('register.sections.identity')}</span>
                                        <span className="register-section-line" aria-hidden="true" />
                                    </div>
                                    <div className="register-grid">
                                        <Form.Item
                                            className="register-field"
                                            label={t('register.fields.name')}
                                            name="name"
                                            rules={[
                                                { required: true, message: t('register.validation.name_required') },
                                                { max: 80, message: t('register.validation.name_max') }
                                            ]}
                                        >
                                            <Input
                                                size="small"
                                                placeholder={t('register.placeholders.name')}
                                                autoComplete="given-name"
                                                disabled={loading}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            className="register-field"
                                            label={t('register.fields.surname')}
                                            name="surname"
                                            rules={[
                                                { required: true, message: t('register.validation.surname_required') },
                                                { max: 80, message: t('register.validation.surname_max') }
                                            ]}
                                        >
                                            <Input
                                                size="small"
                                                placeholder={t('register.placeholders.surname')}
                                                autoComplete="family-name"
                                                disabled={loading}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            className="register-field"
                                            label={t('register.fields.email')}
                                            name="email"
                                            rules={[
                                                { required: true, message: t('register.validation.email_required') },
                                                { type: 'email', message: t('register.validation.email_invalid') },
                                                { max: 254, message: t('register.validation.email_max') }
                                            ]}
                                        >
                                            <Input
                                                size="small"
                                                type="email"
                                                placeholder={t('register.placeholders.email')}
                                                autoComplete="email"
                                                disabled={loading}
                                            />
                                        </Form.Item>

                                        <OrganizationNameField form={form} loading={loading} />
                                    </div>
                                </div>

                                <div className="register-section register-section--organization">
                                    <div className="register-section-header">
                                        <span className="register-section-step">02</span>
                                        <span className="register-section-title">{t('register.sections.organization')}</span>
                                        <span className="register-section-line" aria-hidden="true" />
                                    </div>
                                    <button
                                        className={`register-role-toggle ${showJobFunction ? 'is-open' : ''}`}
                                        type="button"
                                        onClick={() => setShowJobFunction((visible) => !visible)}
                                        aria-expanded={showJobFunction}
                                    >
                                        <PlusOutlined aria-hidden="true" />
                                        <span>{t(showJobFunction ? 'register.flow.hide_role' : 'register.flow.add_role')}</span>
                                    </button>

                                    {showJobFunction && (
                                        <Form.Item
                                            className="register-field register-role-field"
                                            label={t('register.fields.job_function')}
                                            name="jobFunction"
                                            rules={[
                                                { max: 120, message: t('register.validation.job_function_max') }
                                            ]}
                                        >
                                            <Input
                                                size="small"
                                                placeholder={t('register.placeholders.job_function')}
                                                autoComplete="organization-title"
                                                disabled={loading}
                                            />
                                        </Form.Item>
                                    )}
                                </div>

                                        <div className="register-action-zone">
                                            <div className="register-step-note">
                                                <SafetyCertificateOutlined aria-hidden="true" />
                                                <span>{t('register.flow.details_note')}</span>
                                            </div>

                                            <Button
                                                className="login-button register-primary-action"
                                                size="large"
                                                type="primary"
                                                htmlType="submit"
                                                loading={loading}
                                            >
                                                <span>{t('register.flow.continue')}</span>
                                                <ArrowRightOutlined aria-hidden="true" />
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {currentStep === 1 && (
                                    <div className="register-step-panel" key="security">
                                        <div className="register-step-copy">
                                            <span className="register-step-kicker">{t('register.flow.security_eyebrow')}</span>
                                            <h3 ref={stepHeadingRef} tabIndex={-1}>{t('register.flow.security_title')}</h3>
                                            <p>{t('register.flow.security_subtitle')}</p>
                                        </div>

                                <div className="register-section register-section--security">
                                    <div className="register-section-header">
                                        <span className="register-section-step">03</span>
                                        <span className="register-section-title">{t('register.sections.security')}</span>
                                        <span className="register-section-line" aria-hidden="true" />
                                    </div>
                                    <div className="register-grid">
                                        <Form.Item
                                            className="register-field"
                                            label={t('register.fields.password')}
                                            name="password"
                                            rules={passwordRules}
                                        >
                                            <Input.Password
                                                size="small"
                                                placeholder={t('register.placeholders.password')}
                                                autoComplete="new-password"
                                                disabled={loading}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            className="register-field"
                                            label={t('register.fields.confirm_password')}
                                            name="confirmPassword"
                                            dependencies={['password']}
                                            rules={[
                                                { required: true, message: t('register.validation.confirm_required') },
                                                ({ getFieldValue }) => ({
                                                    validator(_, value) {
                                                        if (!value || getFieldValue('password') === value) {
                                                            return Promise.resolve();
                                                        }
                                                        return Promise.reject(new Error(t('register.validation.password_mismatch')));
                                                    }
                                                })
                                            ]}
                                        >
                                            <Input.Password
                                                size="small"
                                                placeholder={t('register.placeholders.confirm_password')}
                                                autoComplete="new-password"
                                                disabled={loading}
                                            />
                                        </Form.Item>
                                    </div>

                                    <PasswordStrengthMeter form={form} />
                                </div>

                                <Form.Item
                                    name="acceptedTerms"
                                    valuePropName="checked"
                                    className="register-terms-item"
                                    rules={[
                                        {
                                            validator: (_, value) => value
                                                ? Promise.resolve()
                                                : Promise.reject(new Error(t('register.validation.terms_required')))
                                        }
                                    ]}
                                >
                                    <Checkbox disabled={loading} className="register-terms">
                                        {t('register.terms.prefix')}{' '}
                                        <a href={TERMS_URL} target="_blank" rel="noreferrer">
                                            {t('register.terms.link')}
                                        </a>
                                    </Checkbox>
                                </Form.Item>

                                <div className="register-reassurance register-reassurance--security">
                                    <SafetyCertificateOutlined aria-hidden="true" />
                                    <span>{t('register.reassurance')}</span>
                                </div>

                                <div className="register-step-actions register-action-zone">
                                    <button
                                        type="button"
                                        className="register-secondary-action"
                                        onClick={handleBackToDetails}
                                        disabled={loading}
                                    >
                                        <ArrowLeftOutlined aria-hidden="true" />
                                        <span>{t('register.flow.back')}</span>
                                    </button>

                                    <Button
                                        className="login-button register-submit register-primary-action"
                                        size="large"
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                    >
                                        <span className="register-submit-label">{t('register.submit')}</span>
                                        {!loading && <ArrowRightOutlined className="register-submit-arrow" />}
                                    </Button>
                                </div>

                                    </div>
                                )}
                            </Form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Register;
